import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const FunnelLandingPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('loading'); // loading | ready | done | invalid
  const [page, setPage] = useState(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/public/funnels/${slug}`)
      .then((r) => { if (mounted) { setPage(r.data); setState('ready'); } })
      .catch(() => { if (mounted) setState('invalid'); });
    return () => { mounted = false; };
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/funnels/${slug}/submit`, {
        email: email.trim(), first_name: firstName.trim(),
        ref: searchParams.get('ref') || searchParams.get('utm_campaign') || '',
      });
      if (r.data?.mode === 'redirect' && r.data?.redirect_url) {
        window.location.href = r.data.redirect_url;
        return;
      }
      setThanks(r.data?.message || "You're in! Check your inbox shortly.");
      setState('done');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const brand = page?.brand_color || '#db2777';

  if (state === 'loading') {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="w-7 h-7 animate-spin text-zinc-400" /></div>;
  }
  if (state === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="text-center" data-testid="funnel-not-found">
          <h1 className="text-2xl font-bold text-zinc-900">Page not found</h1>
          <p className="text-zinc-500 mt-2">This link is no longer available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="funnel-landing">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-zinc-100 overflow-hidden">
        {page.image_url ? (
          <img src={page.image_url} alt="" className="w-full h-48 object-cover" />
        ) : (
          <div className="h-2" style={{ backgroundColor: brand }} />
        )}
        <div className="p-8">
          {state === 'done' ? (
            <div className="text-center py-6" data-testid="funnel-success">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: brand }} />
              <h1 className="text-2xl font-bold text-zinc-900">Thank you!</h1>
              <p className="text-zinc-600 mt-2">{thanks}</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-zinc-900 leading-tight">{page.headline}</h1>
              {page.subtext && <p className="text-zinc-600 mt-3 whitespace-pre-line">{page.subtext}</p>}
              <form onSubmit={submit} className="mt-6 space-y-3">
                {page.collect_first_name && (
                  <input
                    type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name" data-testid="funnel-firstname-input"
                    className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': brand }}
                  />
                )}
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com" required data-testid="funnel-email-input"
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:outline-none focus:ring-2"
                />
                {error && <p className="text-sm text-red-600" data-testid="funnel-error">{error}</p>}
                <button
                  type="submit" disabled={busy} data-testid="funnel-submit-btn"
                  className="w-full text-white font-semibold py-3 rounded-xl transition-opacity hover:opacity-90 inline-flex items-center justify-center disabled:opacity-60"
                  style={{ backgroundColor: brand }}
                >
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : page.button_text}
                </button>
              </form>
              <p className="text-[11px] text-zinc-400 mt-4 text-center">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </>
          )}
        </div>
        <div className="px-8 pb-5 flex items-center justify-center gap-1 text-[11px] text-zinc-300">
          <Sparkles className="w-3 h-3" /> Powered by PostToSocial
        </div>
      </div>
    </div>
  );
};

export default FunnelLandingPage;
